import axios from 'axios'
import { push } from 'connected-react-router'

import { hideLoader } from './drawer.actions'

import * as userApi from 'api/user'
import * as studentApi from 'api/student'
import { ROUTES } from 'routes/paths'

/*
 * Action types
 */

export const ADD_STUDENT_SET_STEP = '@@students/ADD_STUDENT_SET_STEP'
export const CHECK_SCHOOL = '@@students/CHECK_SCHOOL'
export const CHECK_USERNAME = '@@students/CHECK_USERNAME'
export const CREATE_NEW_STUDENT = '@@students/CREATE_NEW_STUDENT'
export const FETCH_STUDENT_PROFILE = '@@students/FETCH_STUDENT_PROFILE'
export const FETCH_STUDENT_COURSES_AVAILABLE =
  '@@students/FETCH_STUDENT_COURSES_AVAILABLE'
export const FETCH_STUDENT_CLASSROOMS_AVAILABLE =
  '@@students/FETCH_STUDENT_CLASSROOMS_AVAILABLE'
export const FETCH_STUDENT_LIST = '@@students/FETCH_STUDENT_LIST'
export const SET_LICENSE = '@@students/SET_LICENSE'
export const REMOVE_LICENSE = '@@students/REMOVE_LICENSE'
export const REMOVE_NEW_STUDENT = '@@students/REMOVE_NEW_STUDENT'
export const UPDATE_NEW_STUDENT = '@@students/UPDATE_NEW_STUDENT'
export const UPDATE_STUDENT_LIST = '@@students/UPDATE_STUDENT_LIST'

/*
 * Steps types
 */

export const steps = {
  ADD_NEW_STUDENT: 'ADD_NEW_STUDENT',
  SET_NEW_STUDENT_CLASSROOM: 'SET_NEW_STUDENT_CLASSROOM',
  SET_NEW_STUDENT_COURSE: 'SET_NEW_STUDENT_COURSE',
  SET_NEW_STUDENT_SCHOOL: 'SET_NEW_STUDENT_SCHOOL',
}

/*
 * Action creators
 */

export const fetchStudentListAction =
  (force = false) =>
  async (dispatch, getState) => {
    const {
      students: { list },
    } = getState()
    if (list.length && !force) {
      return
    }
    dispatch({ type: FETCH_STUDENT_LIST })
    try {
      const data = await studentApi.getChildren()
      dispatch({
        list: data,
        type: UPDATE_STUDENT_LIST,
      })
    } catch (error) {
      console.error(error)
    }
  }

export const fetchStudentProfileAction = (id) => async (dispatch, getState) => {
  const {
    students: { list },
  } = getState()
  dispatch({ type: FETCH_STUDENT_PROFILE })
  try {
    const data = await studentApi.getChildrenId(id)
    const studentList = [...list]
    data.userBooks = data.userBooks?.map(({ ...bookData }) => ({
      ...bookData,
    }))
    const studentIndex = list?.findIndex((student) => student.id === id)
    if (studentIndex >= 0) {
      studentList[studentIndex] = { ...data }
    } else {
      studentList.push(data)
    }
    dispatch({
      list: studentList,
      type: UPDATE_STUDENT_LIST,
    })
  } catch (error) {
    console.error(error)
  }
}

export const setNewStudentAction = (student) => async (dispatch) =>
  dispatch({
    type: UPDATE_NEW_STUDENT,
    userData: student,
  })

export const deleteNewStudentAction =
  (force = false) =>
  async (dispatch, getState) => {
    const {
      students: { newStudent, step },
    } = getState()
    if (!newStudent && !step) {
      return
    }
    dispatch(fetchStudentListAction(force))
    dispatch(push(`${ROUTES.STUDENTS}/${newStudent.id}`))
    dispatch({ type: REMOVE_NEW_STUDENT })
  }

export const checkUsernameAction = (newUsername) => async (dispatch) => {
  dispatch(hideLoader(true))
  let response
  try {
    dispatch({ type: CHECK_USERNAME })
    response = await userApi.checkUserName(newUsername)
  } catch (error) {
    const { data } = error.response || {}
    response = data
  } finally {
    dispatch(hideLoader(false))
  }
  return !!response && response
}

export const fetchCoursesAvailableAction = () => async (dispatch, getState) => {
  const {
    students: { newStudent },
  } = getState()
  if (!newStudent) {
    return
  }
  const { school } = newStudent || {}
  const { id } = school || {}

  dispatch({ type: FETCH_STUDENT_COURSES_AVAILABLE })
  try {
    if (id) {
      const response = await studentApi.courses(id)
      const { data } = response
      if (!data?.length) {
        dispatch(deleteNewStudentAction(true))
      }
      dispatch({
        type: UPDATE_NEW_STUDENT,
        userData: {
          availableCourses: data.sort((a, b) => {
            if (a.order > b.order) {
              return 1
            }
            if (b.order > a.order) {
              return -1
            }
            return 0
          }),
        },
      })
    }
  } catch (error) {
    console.error(error)
  }
}

export const fetchClassroomsAvailableAction =
  () => async (dispatch, getState) => {
    const {
      students: { newStudent },
    } = getState()
    if (!newStudent) {
      return
    }

    const { course, school } = newStudent || {}
    const { id: courseId } = course || {}
    const { id: schoolId, configuredSchoolYear } = school || {}
    const { id: schoolYearId } = configuredSchoolYear || {}

    if (courseId && schoolId && schoolYearId) {
      dispatch({ type: FETCH_STUDENT_CLASSROOMS_AVAILABLE })
      const params = {
        url: `/bk/schools/${schoolId}/classrooms?${schoolYearId}&courseId=${courseId}`,
      }
      try {
        const response = await axios(params)
        const { data } = response
        if (!data?.length) {
          dispatch(deleteNewStudentAction(true))
        }
        dispatch({
          type: UPDATE_NEW_STUDENT,
          userData: {
            availableClassrooms: data.sort((a, b) => {
              if (a.name > b.name) {
                return 1
              }
              if (b.name > a.name) {
                return -1
              }
              return 0
            }),
          },
        })
      } catch (error) {
        console.error(error)
      }
    }
  }

export const setStudentAction = (data) => async (dispatch, getState) => {
  const {
    user: { id },
  } = getState()
  const details = { ...data, parentId: id }
  let newStudent = null
  try {
    const { data } = await studentApi.studentsUnder14(details)
    newStudent = data
    dispatch({
      type: UPDATE_NEW_STUDENT,
      userData: data,
    })
  } catch (error) {
    console.error(error)
  }
  const forceFetching = true
  dispatch(fetchStudentListAction(forceFetching))
  return newStudent
}

export const setLicenseAction = (code, childBookId) => (dispatch) => {
  if (code) {
    dispatch({ license: { childBookId, code }, type: SET_LICENSE })
  }
}

export const removeLicenseAction = () => (dispatch) => {
  dispatch({
    type: REMOVE_LICENSE,
  })
}

export const setSchoolAction = (code) => async (dispatch, getState) => {
  const {
    students: {
      newStudent: { id },
    },
  } = getState()
  try {
    const response = await studentApi.students({
      data: { userId: id },
      schoolCodeId: code,
    })
    dispatch({
      type: UPDATE_NEW_STUDENT,
      userData: { school: response.data },
    })
  } catch (error) {
    console.error(error)
  }
}

export const setCourseAction = (course) => async (dispatch, getState) => {
  const {
    students: {
      newStudent: { id },
    },
  } = getState()
  const { id: courseId } = course
  try {
    await studentApi.updateCourses(id, courseId)
    dispatch({
      type: UPDATE_NEW_STUDENT,
      userData: { course },
    })
  } catch (error) {
    console.error(error)
  }
}

export const setClassroomAction = (classroom) => async (dispatch, getState) => {
  const {
    students: {
      newStudent: { id: userId },
    },
  } = getState()
  const { id: classroomId } = classroom
  try {
    await studentApi.updateClassrooms({ classroomId, data: { userId } })
    dispatch({
      type: UPDATE_NEW_STUDENT,
      userData: { classroom },
    })
  } catch (error) {
    console.error(error)
  }
}

export const setStepAction =
  (newStep = null) =>
  async (dispatch, getState) => {
    const {
      students: { step },
    } = getState()
    if (newStep === step) {
      return
    }
    dispatch({
      step: newStep,
      type: ADD_STUDENT_SET_STEP,
    })
  }

export const patchStudentAction = (id, data, isPersonalData) => async () => {
  await studentApi.setChildDataRelative({ data, id, isPersonalData })
}
